/**
 * DiagonalPhotoOverlay — field-photo of a flange with the surveyor's
 * measured diagonal geometry drawn over it.
 *
 * Two modes:
 *   - **capture** — the surveyor uploads/takes a photo, then taps
 *     each corner (A → B → C → D) to place pins. Pins can be
 *     dragged to fine-tune.
 *   - **print** — read-only rendering used on the PDF report. Same
 *     SVG geometry, but pointer events off and the "tap to place"
 *     helper hidden.
 *
 * SVG overlay (viewBox 0..100 in both axes, absolute-positioned
 * over the photo) draws:
 *   • Corner pins (green filled circles + white A/B/C/D labels)
 *   • Diagonal lines A-C and B-D with the mm length label
 *   • Side lines (A-B, B-C, C-D, D-A) faintly, with mm length labels
 *   • Δdiag callout in the top-left corner
 *   • PASS/FAIL stamp bottom-right
 *   • Height annotation next to each corner (Z coord from the shot)
 *
 * We store the pin positions in the session's `photo_pins` (0..1 U/V
 * space) so the overlay stays correct if the photo is resized.
 */
import React, { useRef, useState } from "react";

const CORNER_NAMES = ["A", "B", "C", "D"];
const PIN_COLOURS = ["#8DC63F", "#8DC63F", "#8DC63F", "#8DC63F"];

// Distance formula in 3D (matches server-side spool_math).
const dist3d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

export function DiagonalPhotoOverlay({
  photoDataUrl,
  pins = [null, null, null, null],  // [{u, v} | null] × 4
  onPinsChange,
  shots = [],                       // 4 shots with x/y/z
  deltaMm,
  withinTol,
  mode = "capture",                 // "capture" | "print"
}) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(null); // corner index

  // "Next corner to place" — first slot that's still null. Once all
  // four are placed, tapping does nothing (drag existing pins to
  // move them). This is what the on-screen helper text keys off.
  const nextIdx = pins.findIndex((p) => p == null);

  const handleTap = (e) => {
    if (mode !== "capture" || !containerRef.current || nextIdx < 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    const u = (clientX - rect.left) / rect.width;
    const v = (clientY - rect.top) / rect.height;
    const next = [...pins];
    next[nextIdx] = { u, v };
    onPinsChange?.(next);
  };

  const handleDragStart = (idx) => (e) => {
    if (mode !== "capture") return;
    e.stopPropagation();
    setDragging(idx);
  };
  const handleDragMove = (e) => {
    if (dragging == null || !containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    const u = Math.max(0.02, Math.min(0.98, (clientX - rect.left) / rect.width));
    const v = Math.max(0.02, Math.min(0.98, (clientY - rect.top) / rect.height));
    const next = [...pins];
    next[dragging] = { u, v };
    onPinsChange?.(next);
  };
  const handleDragEnd = () => setDragging(null);

  // Compute the mm lengths from the 3D shot coords — same math as
  // the plan drawing / PDF summary. We don't derive these from the
  // pin positions on the photo (which are pixel space and lie).
  const len = (i, j) => {
    if (!shots[i] || !shots[j]) return null;
    return dist3d(shots[i], shots[j]) * 1000;  // metres → mm
  };
  const diagAC = len(0, 2);
  const diagBD = len(1, 3);
  const sideAB = len(0, 1);
  const sideBC = len(1, 2);
  const sideCD = len(2, 3);
  const sideDA = len(3, 0);

  // Colour ramp for the delta callout. Green when within tol, red when
  // over. The `withinTol` prop is authoritative — we don't recompute.
  const deltaColour = withinTol == null ? "#8B939A" : withinTol ? "#8DC63F" : "#FF3B30";

  const stopEvents = mode === "capture" ? {} : { style: { pointerEvents: "none" } };

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-md border border-[#2A2E39] bg-[#0A0B0E]"
        style={{ aspectRatio: "4/3" }}
        onClick={handleTap}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        {photoDataUrl ? (
          <img src={photoDataUrl} alt="Flange" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-widest text-[#4A5054]">
            No photo yet
          </div>
        )}

        {/* SVG overlay in 0..100 relative units so it stays correct
            regardless of container size. */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none"
             className="absolute inset-0 h-full w-full" {...stopEvents}>
          {/* Side edges — faint dashed lines between consecutive pins. */}
          {pins[0] && pins[1] && (
            <line x1={pins[0].u * 100} y1={pins[0].v * 100} x2={pins[1].u * 100} y2={pins[1].v * 100}
                  stroke="#F5A623" strokeWidth="0.25" strokeDasharray="1 1" opacity="0.7" />
          )}
          {pins[1] && pins[2] && (
            <line x1={pins[1].u * 100} y1={pins[1].v * 100} x2={pins[2].u * 100} y2={pins[2].v * 100}
                  stroke="#F5A623" strokeWidth="0.25" strokeDasharray="1 1" opacity="0.7" />
          )}
          {pins[2] && pins[3] && (
            <line x1={pins[2].u * 100} y1={pins[2].v * 100} x2={pins[3].u * 100} y2={pins[3].v * 100}
                  stroke="#F5A623" strokeWidth="0.25" strokeDasharray="1 1" opacity="0.7" />
          )}
          {pins[3] && pins[0] && (
            <line x1={pins[3].u * 100} y1={pins[3].v * 100} x2={pins[0].u * 100} y2={pins[0].v * 100}
                  stroke="#F5A623" strokeWidth="0.25" strokeDasharray="1 1" opacity="0.7" />
          )}
          {/* Diagonals A-C and B-D — solid, prominent. */}
          {pins[0] && pins[2] && (
            <line x1={pins[0].u * 100} y1={pins[0].v * 100} x2={pins[2].u * 100} y2={pins[2].v * 100}
                  stroke="#00E1C7" strokeWidth="0.4" />
          )}
          {pins[1] && pins[3] && (
            <line x1={pins[1].u * 100} y1={pins[1].v * 100} x2={pins[3].u * 100} y2={pins[3].v * 100}
                  stroke="#00E1C7" strokeWidth="0.4" />
          )}
          {/* Corner pins + labels. */}
          {pins.map((p, i) => p && (
            <g key={i}>
              <circle cx={p.u * 100} cy={p.v * 100} r="2.2" fill={PIN_COLOURS[i]}
                      stroke="#0A0B0E" strokeWidth="0.4"
                      onMouseDown={handleDragStart(i)} onTouchStart={handleDragStart(i)}
                      style={{ cursor: mode === "capture" ? "grab" : "default", pointerEvents: mode === "capture" ? "auto" : "none" }}
                      data-testid={`diag-pin-${CORNER_NAMES[i]}`} />
              <text x={p.u * 100} y={p.v * 100 + 0.9} textAnchor="middle"
                    fontSize="2.5" fontWeight="900" fill="#0A0B0E" fontFamily="Chakra Petch, sans-serif">
                {CORNER_NAMES[i]}
              </text>
              {shots[i]?.z != null && (
                <text x={p.u * 100 + 3} y={p.v * 100 + 1} fontSize="1.8" fill="#00E1C7"
                      fontFamily="JetBrains Mono, monospace">
                  z={shots[i].z.toFixed(3)}m
                </text>
              )}
            </g>
          ))}
          {/* Diagonal length labels — placed at midpoints. */}
          {pins[0] && pins[2] && diagAC != null && (
            <text x={(pins[0].u + pins[2].u) * 50} y={(pins[0].v + pins[2].v) * 50 - 1.5}
                  textAnchor="middle" fontSize="2.6" fontWeight="700" fill="#00E1C7"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.7" paintOrder="stroke">
              A-C  {diagAC.toFixed(0)}mm
            </text>
          )}
          {pins[1] && pins[3] && diagBD != null && (
            <text x={(pins[1].u + pins[3].u) * 50} y={(pins[1].v + pins[3].v) * 50 + 3}
                  textAnchor="middle" fontSize="2.6" fontWeight="700" fill="#00E1C7"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.7" paintOrder="stroke">
              B-D  {diagBD.toFixed(0)}mm
            </text>
          )}
          {/* Side length labels — midpoint of each side. */}
          {pins[0] && pins[1] && sideAB != null && (
            <text x={(pins[0].u + pins[1].u) * 50} y={(pins[0].v + pins[1].v) * 50 - 1}
                  textAnchor="middle" fontSize="2" fill="#F5A623"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.6" paintOrder="stroke">
              {sideAB.toFixed(0)}
            </text>
          )}
          {pins[1] && pins[2] && sideBC != null && (
            <text x={(pins[1].u + pins[2].u) * 50 + 2} y={(pins[1].v + pins[2].v) * 50}
                  textAnchor="middle" fontSize="2" fill="#F5A623"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.6" paintOrder="stroke">
              {sideBC.toFixed(0)}
            </text>
          )}
          {pins[2] && pins[3] && sideCD != null && (
            <text x={(pins[2].u + pins[3].u) * 50} y={(pins[2].v + pins[3].v) * 50 + 2}
                  textAnchor="middle" fontSize="2" fill="#F5A623"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.6" paintOrder="stroke">
              {sideCD.toFixed(0)}
            </text>
          )}
          {pins[3] && pins[0] && sideDA != null && (
            <text x={(pins[3].u + pins[0].u) * 50 - 2} y={(pins[3].v + pins[0].v) * 50}
                  textAnchor="middle" fontSize="2" fill="#F5A623"
                  fontFamily="JetBrains Mono, monospace"
                  stroke="#0A0B0E" strokeWidth="0.6" paintOrder="stroke">
              {sideDA.toFixed(0)}
            </text>
          )}
          {/* Δdiag callout — top-left. */}
          {deltaMm != null && (
            <g>
              <rect x="2" y="2" width="28" height="8" fill="#0A0B0E" fillOpacity="0.75" rx="1" />
              <text x="16" y="6.5" textAnchor="middle" fontSize="3" fontWeight="900"
                    fill={deltaColour} fontFamily="Chakra Petch, sans-serif">
                Δdiag {deltaMm.toFixed(1)}mm
              </text>
              <text x="16" y="9.5" textAnchor="middle" fontSize="2" fill={deltaColour}
                    fontFamily="Chakra Petch, sans-serif">
                {withinTol == null ? "" : withinTol ? "PASS" : "FAIL"}
              </text>
            </g>
          )}
          {/* Scale bar — bottom-left, calibrated off the longest side.
              Only shown when we have that side placed. */}
          {pins[0] && pins[1] && sideAB != null && (
            <g>
              <line x1="2" y1="97" x2="20" y2="97"
                    stroke="#FFFFFF" strokeWidth="0.7" paintOrder="stroke" />
              <text x="11" y="95.5" textAnchor="middle" fontSize="1.8" fill="#FFFFFF"
                    fontFamily="JetBrains Mono, monospace"
                    stroke="#0A0B0E" strokeWidth="0.5" paintOrder="stroke">
                scale · A-B ≡ {sideAB.toFixed(0)}mm
              </text>
            </g>
          )}
        </svg>

        {/* On-screen helper — hidden in print mode. */}
        {mode === "capture" && nextIdx >= 0 && photoDataUrl && (
          <div className="absolute inset-x-0 bottom-2 flex justify-center">
            <div className="rounded-md border border-[#8DC63F]/50 bg-[#0A0B0E]/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#8DC63F]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Tap corner {CORNER_NAMES[nextIdx]} on the photo
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DiagonalPhotoOverlay;
